// Client-side persistence for the Inquiry Processor. The ONLY thing stored
// locally is the item mirror (InquiryStore) that bridges real API items into
// the Add Items / Add Quote modals — deliberate tech-debt until the modals
// take items as props. Everything the user sees comes from the
// restaurant-inquiries APIs; nothing is seeded or mocked here.
// A tiny pub/sub lets React screens re-render on any write.

import type { Inquiry } from 'pages/inquiry-processor/types';
import { CAT_KW } from 'pages/inquiry-processor/utils/constants';

let _uidC = 0;
export const uid = (): string => Date.now().toString(36) + (++_uidC).toString(36);

export function detectCat(name: string): string {
  const n = (name || '').toLowerCase();
  for (const [cat, kws] of Object.entries(CAT_KW)) {
    if (kws.some((k) => n.includes(k))) return cat;
  }
  return 'Other';
}

// ── change notification ──────────────────────────────────────────────────
type Listener = () => void;
const listeners = new Set<Listener>();
export function subscribe(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function emit() {
  listeners.forEach((fn) => fn());
}

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function write(key: string, value: unknown) {
  localStorage.setItem(key, JSON.stringify(value));
  emit();
}

// ── inquiries (mirror of API data for the modals) ───────────────────────────
const INQ_KEY = 'kaso_inq_v1';
export const InquiryStore = {
  all(): Inquiry[] {
    return read<{ inquiries: Inquiry[] }>(INQ_KEY, { inquiries: [] }).inquiries;
  },
  get(id: string): Inquiry | null {
    return this.all().find((i) => i.id === id) || null;
  },
  upsert(inq: Inquiry) {
    const list = this.all();
    const idx = list.findIndex((i) => i.id === inq.id);
    if (idx >= 0) list[idx] = inq;
    else list.unshift(inq);
    write(INQ_KEY, { inquiries: list });
  },
  del(id: string) {
    write(INQ_KEY, { inquiries: this.all().filter((i) => i.id !== id) });
  },
};
